import React from 'react'
import PropTypes from 'prop-types'
import { Button, withStyles } from '@material-ui/core'
import classNames from 'classnames'
import { Field, Form, useForm, deleteDynamicForm } from '../../form'
import { adminSalaryFormValidate } from '../../containers/validate'
import { useDispatch, useSelector } from 'react-redux'
import { NumberInput } from '../../utils/numberFormat'
import { getCurrentMonthSumUlosotto, getMaxMonthlyGovPay } from '../../selectors/admin.selectors'
import { isCurrentMonth } from '../../utils/DateTimeFormat'

/**
 * @author Pragya
 */

class AdminSalaryForm extends React.Component {
  render() {
    return <AdminSalaryFormComponent {...this.props} />
  }
}

const styles = (theme) => ({
  buttonList: {
    marginRight: '0px !important',
    paddingTop: '0px !important',
    paddingLeft: theme.spacing(2) + 'px !important',
    paddingRight: theme.spacing(2) + 'px !important'
  },
  textField: {
    fontSize: '15px',
    marginTop: theme.spacing(4)
  },
  checkbox: {
    marginTop: theme.spacing(2),
    fontSize: '15px',
    color: '#808080',
    fontWeight: 'bold'
  },
  notes: {
    lineHeight: '150%'
  },
  ulosottoField: {
    display: 'flex'
  },
  ulosottoInfo: {
    display: 'flex', 
    flexDirection: 'column', 
    justifyContent: 'flex-end', 
    alignItems: 'end',
    marginLeft: 15,
    flex: 3
  },
  ulosottoRow: {
    display: 'flex', 
    whiteSpace: 'nowrap'
  },
  ulosottoLabel: {
    minWidth: '80px'
  },
  ulosottoValue: {
    display: 'flex',
    color: '#F67131',
    fontWeight: 'bold',
    minWidth: '60px'
  },
  resize: {
    flex: 1
  }
})

const defaultValues = {
  gross_salary: 0,
  net_salary: 0,
  tax_cost: 0,
  yel_cost: 0,
  social_contribution: 0,
  service_cost: 0,
  mileage_allowance: 0,
  full_time_allowance: 0,
  part_time_allowance: 0,
  meal_allowance: 0,
  daily_allowance: 0,
  reimbursment_cost: 0,
  normal_govpay_sum: 0,
  manual_sum_govpay: 0,
  ulosotto_sum_govpay: 0,
  take_home_pay: 0,
  manual_sum_govpay_update: 0,
  ulosotto_sum_govpay_update: 0,
  normal_govpay_sum_update: 0,
  take_home_pay_update: 0
}

const AdminSalaryFormComponent = ({
  classes,
  index,
  initialValues,
  updateAdminSalary,
  setOpen
}) => {
  const currentMonthSumUlosotto = useSelector(getCurrentMonthSumUlosotto)
  const maxMonthlyGovPay = useSelector(getMaxMonthlyGovPay)
  const isManualGovPay = initialValues.ismanualgovpay
  const manualSumGovPay = initialValues.manual_sum_govpay
  const ulosottoSumGovPay = initialValues.ulosotto_sum_govpay

  let initDefVal = {
    ...defaultValues,
    ...replaceNullsWithStrings(initialValues)
  }

  initDefVal.daily_allowance =
    initialValues.full_time_allowance +
    initialValues.part_time_allowance +
    initialValues.meal_allowance

  initDefVal.manual_sum_govpay_update = manualSumGovPay
  initDefVal.ulosotto_sum_govpay_update = ulosottoSumGovPay

  initDefVal.normal_govpay_sum_update = initialValues.normal_govpay_sum

  initDefVal.take_home_pay_update = initialValues.take_home_pay

  let take_home_with_govpay =
    initialValues.take_home_pay + (isManualGovPay ? manualSumGovPay : ulosottoSumGovPay)

    const adjustedCurrentMonthSumUlosotto = 
      isManualGovPay &&
      isCurrentMonth(initialValues.created)
        ? parseFloat(
          (currentMonthSumUlosotto - (manualSumGovPay ?? 0)).toFixed(2)
        )
        : currentMonthSumUlosotto

  const form = useForm({
    name: 'adminSalaryForm' + index,
    onSubmit: onSubmit,
    defaultValues: initDefVal,
    resetOnSubmit: false,
    validator: (values) => adminSalaryFormValidate(
      values,
      adjustedCurrentMonthSumUlosotto,
      maxMonthlyGovPay,
      isManualGovPay
    )
  })

  const { control } = form

  const dispatch = useDispatch()

  function onSubmit(values) {
    // Server will assign ulosotto type to salary based on salarytype value.
    // before ulosotto type was assigned based on which ulosotto amount was > 0 (ulosotto_sum_govpay_update or manual_sum_govpay_update),
    // but new requirements wants to allow ulosotto amount = 0
    values.salarytype = isManualGovPay ? 'manual_govpay' : 'govpay'
    updateAdminSalary(values)
  }

  function setValue(name, value) {
    form?.setValue(name, value)
  }

  const closeRow = () => {
    setOpen(false)
    dispatch(deleteDynamicForm('adminSalaryForm' + index))
  }

  return (
    <div className={classes.container}>
      <Form {...form}>
        <div className="row">
          <div className="dashboard-content-header">
            <div className="col-xs-12 col-sm-6 col-lg-4">
              <div
                className="panel"
                style={{
                  border: '1px solid #D3D3D3',
                  height: '1000px'
                }}
              >
                <div className="panel-heading">
                  <h3 className="panel-title">{'Palkkatiedot'}</h3>
                </div>
                <div className="panel-body" style={{ marginBottom: '25px' }}>
                  <Field
                    name="gross_salary"
                    inputType="textfield"
                    label="Brutto (€)"
                    className={classNames(classes.textField, classes.resize)}
                    disabled={true}
                    control={control}
                    inputComponent={FixedNumberInput}
                  />
                  <Field
                    name="net_salary"
                    inputType="textfield"
                    label="Netto (€)"
                    className={classNames(classes.textField, classes.resize)}
                    disabled={true}
                    control={control}
                    inputComponent={FixedNumberInput}
                  />
                  <Field
                    name="tax_cost"
                    inputType="textfield"
                    label="Ennakonpidätys (€)"
                    className={classNames(classes.textField, classes.resize)}
                    disabled={true}
                    control={control}
                    inputComponent={FixedNumberInput}
                  />
                  <Field
                    name="yel_cost"
                    inputType="textfield"
                    label="YEL (€)"
                    className={classNames(classes.textField, classes.resize)}
                    disabled={true}
                    control={control}
                    inputComponent={FixedNumberInput}
                  />
                  <Field
                    name="social_contribution"
                    inputType="textfield"
                    label="Sotu (€)"
                    className={classNames(classes.textField, classes.resize)}
                    disabled={true}
                    control={control}
                    inputComponent={FixedNumberInput}
                  />
                  <Field
                    name="service_cost"
                    inputType="textfield"
                    label="PP (€)"
                    className={classNames(classes.textField, classes.resize)}
                    disabled={true}
                    control={control}
                    inputComponent={FixedNumberInput}
                  />
                  <Field
                    name="mileage_allowance"
                    inputType="textfield"
                    label="Km (€)"
                    className={classNames(classes.textField, classes.resize)}
                    disabled={true}
                    control={control}
                    inputComponent={FixedNumberInput}
                  />
                  <Field
                    name="daily_allowance"
                    inputType="textfield"
                    label="Päiväraha (€)"
                    className={classNames(classes.textField, classes.resize)}
                    disabled={true}
                    control={control}
                    inputComponent={FixedNumberInput}
                  />
                  <Field
                    name="reimbursment_cost"
                    inputType="textfield"
                    label="Kuitit (€)"
                    className={classNames(classes.textField, classes.resize)}
                    disabled={true}
                    control={control}
                    inputComponent={FixedNumberInput}
                  />
                  {
                    isManualGovPay ? (
                      <Field
                        name="manual_sum_govpay"
                        inputType="textfield"
                        label="Uo (€)"
                        className={classNames(classes.textField, classes.resize)}
                        disabled={true}
                        control={control}
                        inputComponent={FixedNumberInput}
                      />
                    ) : (
                      <Field
                        name="ulosotto_sum_govpay"
                        inputType="textfield"
                        label="Uo (€)"
                        className={classNames(classes.textField, classes.resize)}
                        disabled={true}
                        control={control}
                        inputComponent={FixedNumberInput}
                      />
                    )
                  }
                  <Field
                    name="take_home_pay"
                    inputType="textfield"
                    label="Maksetaan (€)"
                    className={classNames(classes.textField, classes.resize)}
                    disabled={true}
                    control={control}
                    inputComponent={FixedNumberInput}
                  />
                </div>
              </div>
            </div>
            <div className="col-xs-12 col-sm-6 col-lg-4">
              <div
                className="panel"
                style={{
                  border: '1px solid #D3D3D3',
                  height: '1000px'
                }}
              >
                <div className="panel-heading">
                  <h3 className="panel-title">{'Ulosotto muokkaa palkkaa'}</h3>
                </div>
                <div className="panel-body" style={{ marginBottom: '25px' }}>
                  <Field
                    name="gross_salary"
                    inputType="textfield"
                    label="Brutto (€)"
                    className={classNames(classes.textField, classes.resize)}
                    disabled={true}
                    control={control}
                    inputComponent={FixedNumberInput}
                  />
                  <Field
                    name="net_salary"
                    inputType="textfield"
                    label="Netto (€)"
                    className={classNames(classes.textField, classes.resize)}
                    disabled={true}
                    control={control}
                    inputComponent={FixedNumberInput}
                  />
                  <Field
                    name="tax_cost"
                    inputType="textfield"
                    label="Ennakonpidätys (€)"
                    className={classNames(classes.textField, classes.resize)}
                    disabled={true}
                    control={control}
                    inputComponent={FixedNumberInput}
                  />
                  <Field
                    name="yel_cost"
                    inputType="textfield"
                    label="YEL (€)"
                    className={classNames(classes.textField, classes.resize)}
                    disabled={true}
                    control={control}
                    inputComponent={FixedNumberInput}
                  />
                  <Field
                    name="social_contribution"
                    inputType="textfield"
                    label="Sotu (€)"
                    className={classNames(classes.textField, classes.resize)}
                    disabled={true}
                    control={control}
                    inputComponent={FixedNumberInput}
                  />
                  <Field
                    name="service_cost"
                    inputType="textfield"
                    label="PP (€)"
                    className={classNames(classes.textField, classes.resize)}
                    disabled={true}
                    control={control}
                    inputComponent={FixedNumberInput}
                  />
                  <Field
                    name="mileage_allowance"
                    inputType="textfield"
                    label="Km (€)"
                    className={classNames(classes.textField, classes.resize)}
                    disabled={true}
                    control={control}
                    inputComponent={FixedNumberInput}
                  />
                  <Field
                    name="daily_allowance"
                    inputType="textfield"
                    label="Päiväraha (€)"
                    className={classNames(classes.textField, classes.resize)}
                    disabled={true}
                    control={control}
                    inputComponent={FixedNumberInput}
                  />
                  <Field
                    name="reimbursment_cost"
                    inputType="textfield"
                    label="Kuitit (€)"
                    className={classNames(classes.textField, classes.resize)}
                    disabled={true}
                    control={control}
                    inputComponent={FixedNumberInput}
                  />
                  {
                    isManualGovPay ? (
                      <div className={classes.ulosottoField}>
                        <Field
                          name="manual_sum_govpay_update"
                          inputType="textfield"
                          label="Uo (€)"
                          className={classNames(classes.textField, classes.resize)}
                          style={{ flex: 2 }}
                          control={control}
                          inputComponent={FixedNumberInput}
                          onChange={(e) => {
                            const val = e.target.value
                            setValue(
                              'take_home_pay_update',
                              take_home_with_govpay - val
                            )
                          }}
                        />
                          <div className={classes.ulosottoInfo}>
                            <div className={classes.ulosottoRow}>
                              <span className={classes.ulosottoLabel}>Peritty:</span>
                              <span className={classes.ulosottoValue}>
                                {adjustedCurrentMonthSumUlosotto?.toString().replace('.', ',') ?? 0}
                              </span>
                            </div>
                            <div className={classes.ulosottoRow}>
                              <span className={classes.ulosottoLabel}>Maksimi:</span>
                              <span className={classes.ulosottoValue}>
                                {maxMonthlyGovPay?.toString().replace('.', ',') ?? 0}
                              </span>
                            </div>
                          </div>
                      </div>
                    ) : (
                      <Field
                        name="ulosotto_sum_govpay_update"
                        inputType="textfield"
                        label="Uo (€)"
                        className={classNames(classes.textField, classes.resize)}
                        style={{ flex: 2 }}
                        control={control}
                        inputComponent={FixedNumberInput}
                        onChange={(e) => {
                          const val = e.target.value
                          setValue(
                            'take_home_pay_update',
                            take_home_with_govpay - val
                          )
                        }}
                      />
                    )
                  }
                  <Field
                    name="take_home_pay_update"
                    inputType="textfield"
                    label="Maksetaan (€)"
                    className={classNames(classes.textField, classes.resize)}
                    disabled={true}
                    control={control}
                    inputComponent={FixedNumberInput}
                  />
                </div>
              </div>
            </div>
            <div className="col-xs-12 col-sm-6 col-lg-4">
              <div
                className="panel"
                style={{
                  border: '1px solid #D3D3D3',
                  height: '1000px'
                }}
              >
                <div className="panel-heading">
                  <h3 className="panel-title">{'Asiakastiedot'}</h3>
                </div>
                <div className="panel-body" style={{ marginBottom: '25px' }}>
                  <Field
                    name="lastname"
                    inputType="textfield"
                    label="Sukunimi"
                    className={classNames(classes.textField, classes.resize)}
                    disabled={true}
                    control={control}
                  />
                  <Field
                    name="firstname"
                    inputType="textfield"
                    label="Nimi"
                    className={classNames(classes.textField, classes.resize)}
                    disabled={true}
                    control={control}
                  />
                  <Field
                    name="email"
                    inputType="textfield"
                    label="Sahköposti"
                    className={classNames(classes.textField, classes.resize)}
                    disabled={true}
                    control={control}
                  />
                  <Field
                    name="withdrawal_account_number"
                    inputType="textfield"
                    label="Ulosotto tilinumero"
                    className={classNames(classes.textField, classes.resize)}
                    disabled={true}
                    control={control}
                  />
                  <Field
                    name="account_number"
                    inputType="textfield"
                    label="Henkilökohtainen tilinumero"
                    className={classNames(classes.textField, classes.resize)}
                    disabled={true}
                    control={control}
                  />
                  <Field
                    name="withdrawal_reference_number"
                    inputType="textfield"
                    label="Ulosotto viitenumero (reference number)"
                    className={classNames(classes.textField, classes.resize)}
                    disabled={true}
                    control={control}
                  />
                  <Field
                    name="payment_ban_number"
                    inputType="textfield"
                    label="Maksukiellon numero"
                    className={classNames(classes.textField, classes.resize)}
                    disabled={true}
                    control={control}
                  />
                  <Field
                    name="notes"
                    inputType="textfield"
                    label="Lisäkommentit"
                    className={classNames(
                      classes.textField,
                      classes.resize,
                      classes.notes
                    )}
                    multiline
                    control={control}
                  />
                </div>
              </div>
            </div>
            <div className="row button-align">
              <ul
                className={classNames(
                  classes.buttonList,
                  'button-container nav nav-pills pull-right'
                )}
              >
                <li className="button-list">
                  <Button
                    variant="contained"
                    className="button-fill"
                    onClick={closeRow}
                  >
                    Peruuta
                  </Button>
                </li>
                <li className="button-list">
                  <Button
                    variant="contained"
                    className="button-fill"
                    type="submit"
                  >
                    Tallenna
                  </Button>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </Form>
    </div>
  )
}

function replaceNullsWithStrings(obj) {
  const newObj = {}
  Object.keys(obj).forEach((key) => {
    newObj[key] = obj[key] === null ? '' : obj[key]
  })
  return newObj
}

const FixedNumberInput = (props) => {
  return <NumberInput {...props} decimalScale={2} fixedDecimalScale={false} />
}

AdminSalaryForm.propTypes = {
  classes: PropTypes.object.isRequired
}

export default withStyles(styles)(AdminSalaryForm)